import { useId, useRef, useState, type FormEvent } from 'react';
import { ReviewDialog } from '../../payroll-review/components/ReviewDialog';

interface Props {
  busy: boolean;
  error: string | null;
  /** Called with the trimmed reason; never for a blank one. */
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

/** Discard a run that has not been sent for approval. Nothing is deleted: the run stays in the history as Discarded, read-only. A reason is required. */
export function DiscardRunDialog({ busy, error, onConfirm, onCancel }: Props) {
  const reasonId = useId();
  const errorId = useId();
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = reason.trim();
    if (!trimmed) {
      setProblem('Enter a reason for discarding this run.');
      reasonRef.current?.focus();
      return;
    }
    setProblem(null);
    onConfirm(trimmed);
  }

  return (
    <ReviewDialog
      title="Discard this custom pay run?"
      busy={busy}
      onClose={onCancel}
      initialFocusRef={reasonRef}
      description={
        <>
          <p>
            The run stops here and cannot be edited or sent again. It stays in the run history, marked <strong>Discarded</strong>, with your reason. Nothing is
            paid and nothing is deleted.
          </p>
          <p className="muted">To stop a run that was already sent for approval, reject it in Payroll Review instead.</p>
        </>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        <label htmlFor={reasonId} className="payroll-review__label">
          Reason for discarding <span aria-hidden="true">*</span>
          <span className="payroll-review__sr-only"> (required)</span>
        </label>
        <textarea
          id={reasonId}
          ref={reasonRef}
          rows={3}
          maxLength={500}
          value={reason}
          required
          aria-required="true"
          aria-invalid={problem ? true : undefined}
          aria-describedby={problem ? errorId : undefined}
          disabled={busy}
          onChange={(e) => {
            setReason(e.target.value);
            if (problem) setProblem(null);
          }}
        />
        {problem && (
          <p id={errorId} className="error" role="alert">
            {problem}
          </p>
        )}
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <div className="payroll-review__dialog-actions">
          <button type="button" disabled={busy} onClick={onCancel}>
            Keep this run
          </button>
          <button type="submit" className="payroll-review__danger" disabled={busy}>
            {busy ? 'Discarding…' : 'Discard run'}
          </button>
        </div>
      </form>
    </ReviewDialog>
  );
}
